import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import { BiSolidTimer, BiRefresh } from 'react-icons/bi';
import { toast } from 'react-toastify';
import { useLocation, useNavigate } from 'react-router-dom';
import Loader from '../../common/Loader';
import { API_URL, config } from '../../config/config';

const ReadLogs = () => {
	const [logFiles, setLogFiles] = useState([]);
	const [loading, setLoading] = useState(true);
	const [totalPages, setTotalPages] = useState(1);
	const [fileLoading, setFileLoading] = useState(false);
	const [hasFetched, setHasFetched] = useState(false);
	const [file, setFile] = useState({
		fileContent: null,
		fileName: '',
	});
	const [reverse, setReverse] = useState(false);
	const [filterLevel, setFilterLevel] = useState('');
	const [timeFrom, setTimeFrom] = useState('');
	const [timeTo, setTimeTo] = useState('');
	const navigate = useNavigate();
	const location = useLocation();

	// Get page number from URL query params
	const queryParams = new URLSearchParams(location.search);
	const pageFromUrl = queryParams.get('page');
	const initialPage = pageFromUrl ? parseInt(pageFromUrl, 10) : 1;

	const [currentPage, setCurrentPage] = useState(initialPage);

	useEffect(() => {
		const fetchLogFiles = async () => {
			if (!hasFetched) {
				try {
					setLoading(true);
					const response = await axios.post(
						`${API_URL}/logs/get/all-logfiles?page=${currentPage}`,
						{},
						config
					);
					setLogFiles(response?.data?.logFiles);
					setTotalPages(response?.data?.totalPages);
					setHasFetched(true);
				} catch (error) {
					toast.error('Failed to load log files, please try again later.');
				} finally {
					setLoading(false);
				}
			}
		};

		fetchLogFiles();
	}, [currentPage, hasFetched]);

	const fetchFiles = (page) => {
		navigate(`/read-logs/?page=${page}`);
		setCurrentPage(page);
		setHasFetched(false);
	};

	// Handler for pagination
	const handleNextPage = () => {
		if (currentPage < totalPages) {
			const newPage = currentPage + 1;
			setCurrentPage(newPage);
			fetchFiles(newPage);
		}
	};

	const handlePrevPage = () => {
		if (currentPage > 1) {
			const newPage = currentPage - 1;
			setCurrentPage(newPage);
			fetchFiles(newPage);
		}
	};

	const fetchFileContent = async (fileName) => {
		try {
			setFileLoading(true);
			const response = await axios.post(`${API_URL}/logs/get/file-content`, { fileName }, config);

			if (response.data.success) {
				setFile({
					fileContent: response?.data?.content,
					fileName: response?.data?.fileName,
				});
				toast.success('Successfully fetched file content.');
			} else {
				toast.error('Failed to fetch file content.');
			}
			setFileLoading(false);
		} catch (error) {
			setFileLoading(false);
			toast.error('Error fetching file content.');
		}
	};

	const parseLogContent = (logContent) => {
		const logs = logContent?.split('\n') || [];
		const parsedLogs = logs
			.map((log) => {
				const parts = log.split(' | ');
				if (parts.length === 5) {
					const [timestamp, level, description, fileName, lineNumber] = parts;
					return {
						timestamp,
						level,
						description,
						fileName,
						lineNumber,
					};
				}
				return null;
			})
			.filter(Boolean)
			.filter(
				(log) =>
					(!filterLevel || log.level === filterLevel) &&
					(!timeFrom || log.timestamp.split(' ')[1] >= timeFrom) &&
					(!timeTo || log.timestamp.split(' ')[1] <= timeTo)
			);
		return reverse ? parsedLogs.reverse() : parsedLogs;
	};

	return (
		<div className='container mx-auto px-4 sm:px-6 lg:px-8 py-6'>
			<div>
				<h1 className='text-3xl font-semibold text-center mb-6 text-gray-800'>Log Files</h1>

				{loading ? (
					<Loader />
				) : (
					<div>
						<ul className='space-y-3'>
							{logFiles.map((file, index) => (
								<li
									key={index}
									className='p-3 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 cursor-pointer flex items-center justify-between'>
									<span>{file}</span>
									<button
										onClick={() => {
											fetchFileContent(file); // Pass the file directly to fetch content
										}}
										className='py-2 px-4 text-white rounded-md bg-blue-600 hover:bg-blue-700'>
										Get Content
									</button>
								</li>
							))}
						</ul>

						{/* Pagination */}
						{totalPages > 1 && (
							<div className='flex justify-center items-center mt-8'>
								<div className='flex items-center gap-4'>
									<button
										onClick={handlePrevPage}
										disabled={currentPage === 1}
										className={`w-10 h-10 flex items-center justify-center rounded-full ${
											currentPage === 1
												? 'bg-gray-300 text-gray-500 cursor-not-allowed'
												: 'bg-blue-600 text-white hover:bg-blue-700'
										}`}>
										<FiChevronLeft />
									</button>
									<div className='text-gray-700 text-lg'>
										Page {currentPage} of {totalPages}
									</div>
									<button
										onClick={handleNextPage}
										disabled={currentPage === totalPages}
										className={`w-10 h-10 flex items-center justify-center rounded-full ${
											currentPage === totalPages
												? 'bg-gray-300 text-gray-500 cursor-not-allowed'
												: 'bg-blue-600 text-white hover:bg-blue-700'
										}`}>
										<FiChevronRight />
									</button>
								</div>
							</div>
						)}

						{/* Displaying File Content as a Table */}
						
						{file?.fileContent && (
							<div className='mt-6 p-4 bg-white border border-gray-300 rounded-md shadow-sm'>
								<div className='flex justify-between items-center mb-4'>
									<h3 className='font-semibold text-lg'>File Content: {file?.fileName}</h3>
									<div>
										<button
											onClick={() => fetchFileContent(file.fileName)}
											className='py-2 px-4 bg-green-600 text-white rounded-md'>
											{fileLoading ? <Loader /> : <BiRefresh className=' text-xl' />}
										</button>
										<button
											onClick={() => setReverse(!reverse)}
											className='ml-2 py-2 px-4 bg-gray-600 text-white rounded-md'>
											<BiSolidTimer className=' text-xl' />
										</button>
										<select
											onChange={(e) => setFilterLevel(e.target.value)}
											className='ml-2 py-2 px-4 border rounded-md'>
											<option value=''>All</option>
											<option value='INFO'>INFO</option>
											<option value='ERROR'>ERROR</option>
											<option value='WARN'>WARN</option>
										</select>
										<input
											type='time'
											onChange={(e) => setTimeFrom(e.target.value)}
											className='ml-2 py-2 px-4 border rounded-md'
										/>
										<input
											type='time'
											onChange={(e) => setTimeTo(e.target.value)}
											className='ml-2 py-2 px-4 border rounded-md'
										/>
									</div>
								</div>
								<table className='min-w-full table-auto'>
									<thead>
										<tr className='bg-gray-100'>
											<th className='px-4 py-2 text-left border'>Date Time</th>
											<th className='px-4 py-2 text-left border'>Level</th>
											<th className='px-4 py-2 text-left border'>Description</th>
											<th className='px-4 py-2 text-left border'>File Name</th>
											<th className='px-4 py-2 text-left border'>Line Number</th>
										</tr>
									</thead>
									<tbody>
										{parseLogContent(file?.fileContent).map((log, index) => (
											<tr key={index} className='border-b'>
												<td className='px-4 py-2 border'>{log?.timestamp}</td>
												<td className='px-4 py-2 border'>{log?.level}</td>
												<td className='px-4 py-2 border'>{log?.description}</td>
												<td className='px-4 py-2 border'>{log?.fileName}</td>
												<td className='px-4 py-2 border'>{log?.lineNumber}</td>
											</tr>
										))}
									</tbody>
								</table>
							</div>
						)}
					</div>
				)}
			</div>
		</div>
	);
};

export default ReadLogs;
